import React, { CSSProperties, FormEvent, useState } from 'react';
import sb1Logo from 'src/assets/sparebank_hallingdal_logo.png';
import { useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import axios from 'axios';
import { validateEmail } from 'src/utils/validateEmail';

const API = axios.create({ baseURL: process.env.REACT_APP_BASE_API_URL });

/* ── Design tokens ── */
const C = {
  red: '#E3000F',
  redDark: '#B8000C',
  redGlass: 'rgba(227,0,15,0.09)',
  ink: '#151515',
  ink70: 'rgba(21,21,21,0.7)',
  ink40: 'rgba(21,21,21,0.4)',
  warmBg: '#F6F4F0',
  card: '#FFFFFF',
  border: '#E6E0D8',
};

/* ── Styles ── */
const S: Record<string, CSSProperties> = {
  page: { fontFamily: "'DM Sans', -apple-system, sans-serif", background: C.warmBg, color: C.ink, minHeight: '100vh', overflowX: 'hidden' },
  scene: { position: 'relative', width: '100%', height: 'clamp(300px, 38vw, 420px)', background: 'linear-gradient(175deg, #D5E8F0 0%, #A4C2D0 55%, #8BADB8 100%)', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  sceneBrand: { position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px 0' },
  heading: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(26px, 5.5vw, 44px)', fontWeight: 300, color: C.ink, textAlign: 'center', lineHeight: 1.15, letterSpacing: '-0.025em', margin: 0 },
  sub: { marginTop: 8, fontSize: 13, fontWeight: 300, color: C.ink70, letterSpacing: '0.04em' },
  mountains: { position: 'absolute', bottom: 0, left: 0, right: 0, width: '100%', lineHeight: 0 },
  main: { position: 'relative', zIndex: 10, padding: '0 16px 64px', marginTop: -28 },
  card: { maxWidth: 460, margin: '0 auto', background: C.card, borderRadius: 18, boxShadow: '0 2px 4px rgba(21,21,21,0.04), 0 8px 24px rgba(21,21,21,0.09), 0 32px 64px rgba(21,21,21,0.07)', overflow: 'hidden' },
  strip: { height: 3, background: `linear-gradient(90deg, ${C.red} 0%, #FF4020 60%, ${C.red} 100%)` },
  cardHeader: { padding: '22px 28px 18px', borderBottom: `1px solid ${C.border}` },
  cardTitle: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 21, fontWeight: 600, letterSpacing: '-0.02em', color: C.ink, marginBottom: 3 },
  cardDesc: { fontSize: 13, fontWeight: 300, color: C.ink70, lineHeight: 1.5 },
  cardBody: { padding: '24px 28px 28px' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  field: { marginBottom: 18 },
  label: { display: 'block', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.09em', textTransform: 'uppercase' as const, color: C.ink70, marginBottom: 7, userSelect: 'none' as const },
  opt: { fontSize: 10, fontWeight: 300, color: C.ink40, textTransform: 'none' as const, letterSpacing: 0, marginLeft: 5 },
  input: { display: 'block', width: '100%', padding: '11px 14px', fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: C.ink, background: C.warmBg, border: `1.5px solid ${C.border}`, borderRadius: 9, outline: 'none', appearance: 'none' as const, boxSizing: 'border-box' as const, transition: 'border-color 0.25s, box-shadow 0.25s' },
  inputFocus: { borderColor: C.red, background: '#fff', boxShadow: `0 0 0 3px ${C.redGlass}` },
  btn: { width: '100%', padding: '15px 20px', fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, letterSpacing: '0.015em', color: '#fff', background: C.red, border: 'none', borderRadius: 9, cursor: 'pointer', transition: 'background 0.25s', marginTop: 6 },
  btnDisabled: { background: C.redDark, cursor: 'wait' },
  error: { marginTop: 14, padding: '11px 14px', background: 'rgba(227,0,15,0.07)', border: '1px solid rgba(227,0,15,0.22)', borderRadius: 8, fontSize: 13, lineHeight: 1.5, color: C.redDark },
  privacy: { marginTop: 18, fontSize: 11, fontWeight: 300, color: C.ink40, lineHeight: 1.6, textAlign: 'center' as const },
  success: { padding: '44px 28px 40px', textAlign: 'center' as const },
  successTitle: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 30, fontWeight: 400, color: C.ink, letterSpacing: '-0.025em' },
  successRule: { width: 36, height: 2.5, background: C.red, borderRadius: 2, margin: '14px auto' },
  successBody: { fontSize: 14, fontWeight: 300, color: C.ink70, lineHeight: 1.65, maxWidth: 300, margin: '0 auto' },
  footer: { maxWidth: 460, margin: '18px auto 0', textAlign: 'center' as const, fontSize: 11, color: C.ink40 },
};

/* ── Reusable field ── */
type FieldProps = {
  label: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  autoComplete?: string;
  optional?: boolean;
  value: string;
  onChange: (v: string) => void;
};

const Field = ({ label, type = 'text', inputMode, placeholder, autoComplete, optional, value, onChange }: FieldProps) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={S.field}>
      <label style={S.label}>
        {label}{optional ? <span style={S.opt}>(valgfritt)</span> : ' *'}
      </label>
      <input
        style={{ ...S.input, ...(focused ? S.inputFocus : {}) }}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
};

/* ── Animated check ── */
const CheckIcon = () => (
  <svg viewBox="0 0 76 76" fill="none" width={76} height={76} style={{ margin: '0 auto 22px', display: 'block' }}>
    <style>{`
      .cl{stroke-dasharray:210;stroke-dashoffset:210;animation:dc .65s .05s ease forwards}
      .tk{stroke-dasharray:52;stroke-dashoffset:52;animation:dl .45s .62s ease forwards}
      @keyframes dc{to{stroke-dashoffset:0}}@keyframes dl{to{stroke-dashoffset:0}}
    `}</style>
    <circle className="cl" cx="38" cy="38" r="33" stroke={C.red} strokeWidth="2.5" fill="rgba(227,0,15,0.05)" />
    <path className="tk" d="M24 38 L33 47 L52 29" stroke={C.red} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Mountain SVG ── */
const Mountains = () => (
  <div style={S.mountains}>
    <svg viewBox="0 0 1440 220" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMax slice" style={{ display: 'block', width: '100%' }}>
      <defs>
        <linearGradient id="snow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EEF5F8" /><stop offset="80%" stopColor="rgba(238,245,248,0)" />
        </linearGradient>
      </defs>
      <path d="M0,135 C80,95 140,120 210,72 C270,30 350,90 440,55 C510,25 590,85 670,42 C750,2 840,78 920,38 C1000,0 1100,70 1180,40 C1250,12 1350,85 1440,60 L1440,220 L0,220 Z" fill="#8FAEBA" opacity="0.65" />
      <path d="M210,72 L188,108 L232,108 Z" fill="url(#snow)" opacity="0.75" />
      <path d="M440,55 L416,96 L464,96 Z" fill="url(#snow)" opacity="0.70" />
      <path d="M670,42 L644,88 L696,88 Z" fill="url(#snow)" opacity="0.80" />
      <path d="M920,38 L892,82 L948,82 Z" fill="url(#snow)" opacity="0.75" />
      <path d="M0,160 C60,125 120,152 200,102 C280,52 360,128 450,82 C520,42 608,115 690,68 C768,24 860,105 940,62 C1020,20 1110,108 1200,70 C1280,38 1380,130 1440,105 L1440,220 L0,220 Z" fill="#4A7282" opacity="0.90" />
      <path d="M200,102 L180,132 L220,132 Z" fill="url(#snow)" opacity="0.65" />
      <path d="M450,82 L428,116 L472,116 Z" fill="url(#snow)" opacity="0.60" />
      <path d="M690,68 L666,106 L714,106 Z" fill="url(#snow)" opacity="0.70" />
      <path d="M0,188 C50,168 100,178 160,154 C220,130 290,170 370,148 C450,126 530,168 610,145 C690,122 770,165 855,145 C940,125 1020,168 1100,148 C1180,128 1280,172 1360,155 C1400,146 1430,162 1440,158 L1440,220 L0,220 Z" fill="#24505E" />
      <rect x="0" y="216" width="1440" height="4" fill="#1A3E4A" />
    </svg>
  </div>
);

/* ── Form state ── */
type FormState = { firstName: string; lastName: string; email: string; phone: string; street: string; zipCode: string };
const EMPTY: FormState = { firstName: '', lastName: '', email: '', phone: '', street: '', zipCode: '' };

/* ── Page ── */
export default function SB1Halling() {
  const [searchParams] = useSearchParams();
  const agencyId = searchParams.get('agency') ?? '';
  const bankId = searchParams.get('bank') ?? '';
  const leadsFlowConfigId = searchParams.get('config') ?? '';

  const [form, setForm] = useState<FormState>(EMPTY);
  const set = (k: keyof FormState) => (v: string) => setForm(f => ({ ...f, [k]: v }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim() || !form.lastName.trim()) return setError('Fornavn og etternavn er påkrevd.');
    if (!validateEmail(form.email.trim()).isValid) return setError('Skriv inn en gyldig e-postadresse.');
    if (!form.phone.trim()) return setError('Telefonnummer er påkrevd.');
    if (!form.zipCode.trim()) return setError('Postnummer er påkrevd.');

    setLoading(true);
    try {
      await API.post('/public-lead/sb1-halling', {
        agencyId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        street: form.street.trim() || undefined,
        zipCode: form.zipCode.trim(),
        leadsFlowConfigId: leadsFlowConfigId.trim() || undefined,
        bankId: bankId.trim() || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      const status = err?.response?.status ?? 0;
      if (msg) setError(msg);
      else if (status === 429) setError('For mange forsøk. Prøv igjen om litt.');
      else if (status >= 500) setError('Det oppstod en serverfeil. Prøv igjen om litt, eller ring oss.');
      else setError('Noe gikk galt. Prøv igjen, eller ta kontakt direkte.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <Helmet>
        <title>Kom i gang — SpareBank 1 Halling &amp; Valdres</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap" rel="stylesheet" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>

      <header style={S.scene}>
        <div style={S.sceneBrand}>
          <img src={sb1Logo} alt="SpareBank 1 Halling &amp; Valdres" style={{ height: 56, marginBottom: 24, objectFit: 'contain' }} />
          <h1 style={S.heading}>
            La oss hjelpe deg med<br />ditt <em style={{ fontStyle: 'italic', color: C.red }}>boligkjøp</em>
          </h1>
          <p style={S.sub}>Registrer deg — vi tar kontakt</p>
        </div>
        <Mountains />
      </header>

      <main style={S.main}>
        <div style={S.card}>
          <div style={S.strip} />

          {success ? (
            <div style={S.success}>
              <CheckIcon />
              <h2 style={S.successTitle}>Takk skal du ha!</h2>
              <div style={S.successRule} />
              <p style={S.successBody}>
                Vi har mottatt din henvendelse. En rådgiver fra SpareBank&nbsp;1&nbsp;Halling&nbsp;&amp;&nbsp;Valdres
                vil ta kontakt med deg innen kort tid.
              </p>
            </div>
          ) : (
            <>
              <div style={S.cardHeader}>
                <div style={S.cardTitle}>Registrer interesse</div>
                <div style={S.cardDesc}>Fyll ut skjemaet, så kontakter en rådgiver deg snart</div>
              </div>
              <div style={S.cardBody}>
                <form onSubmit={handleSubmit} noValidate>
                  <div style={S.row2}>
                    <Field label="Fornavn" value={form.firstName} onChange={set('firstName')} placeholder="Kari" autoComplete="given-name" />
                    <Field label="Etternavn" value={form.lastName} onChange={set('lastName')} placeholder="Nordmann" autoComplete="family-name" />
                  </div>
                  <Field label="E-postadresse" type="email" value={form.email} onChange={set('email')} placeholder="kari@eksempel.no" autoComplete="email" />
                  <Field label="Telefonnummer" type="tel" value={form.phone} onChange={set('phone')} placeholder="900 00 000" autoComplete="tel" />
                  <div style={S.row2}>
                    <Field label="Adresse" value={form.street} onChange={set('street')} placeholder="Storgata 1" autoComplete="street-address" optional />
                    <Field label="Postnummer" value={form.zipCode} onChange={set('zipCode')} placeholder="3570" autoComplete="postal-code" inputMode="numeric" />
                  </div>

                  <div style={S.field}>
                    <button type="submit" disabled={loading} style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}>
                      {loading ? 'Sender…' : 'Send inn →'}
                    </button>
                    {error && <div style={S.error}>{error}</div>}
                  </div>
                </form>

                <p style={S.privacy}>
                  Dine personopplysninger behandles i samsvar med SpareBank 1 Halling &amp; Valdres
                  sin personvernpolicy og slettes etter at saken er avsluttet.
                </p>
              </div>
            </>
          )}
        </div>

        <div style={S.footer}>© SpareBank 1 Halling &amp; Valdres</div>
      </main>
    </div>
  );
}
